/**
 * AI Assistant Action Hooks
 *
 * Convenience wrappers around dispatch for multi-command actions.
 */

import { useCallback } from 'react';
import { useAIAssistantDispatch, useAIAssistantSelector } from './state/store';
import { seedChannelPickerGreeting } from './state/onboardingSeed';
import type { AIAssistantPanelMode } from './state/state';


/**
 * Provides dispatch-wrapper actions for the AI assistant.
 * Replaces the old context convenience methods.
 */
export function useAIAssistantActions() {
  const dispatch = useAIAssistantDispatch();
  const isProcessing = useAIAssistantSelector( s => s.isProcessing );

  const togglePanel = useCallback(() => {
    dispatch(
      { type: 'aiAssistant/panel/toggle', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  /**
   * Open the panel — IDEMPOTENT, unlike `togglePanel` (TV1-2385 risk 6). Any
   * caller that wants the assistant reachable "from elsewhere" (onboarding,
   * the setup checklist) should call this instead of the
   * `if ( !isPanelOpen ) togglePanel()` idiom — it drops the `isPanelOpen`
   * selector subscription entirely.
   */
  const openPanel = useCallback(( placeholderKey?: string ) => {
    dispatch(
      { type: 'aiAssistant/panel/open', payload: { placeholderKey }},
      { source: 'user' }
    );
  }, [dispatch]);

  const setPanelMode = useCallback(( mode: AIAssistantPanelMode ) => {
    dispatch(
      { type: 'aiAssistant/panel/setMode', payload: { mode }},
      { source: 'user' }
    );
  }, [dispatch]);

  /**
   * Seed the first-login welcome turn.
   *
   * Thin wrapper: the copy and the empty-conversation guard live in
   * `state/onboardingSeed.ts`, because the onboarding wizard's policy has to
   * seed too and a policy cannot call a hook. See that file for WHICH greeting
   * is correct and why that decision does not belong here.
   */
  const seedOnboarding = useCallback(() => {
    seedChannelPickerGreeting();
  }, []);

  const sendMessage = useCallback(( content: string ) => {
    if ( content.trim() === '' || isProcessing ) return;

    const timestamp = Date.now();
    const userMsgId = `msg-${timestamp}-${Math.random().toString( 36 )
.slice( 2, 8 )}`;
    const assistantMsgId = `msg-${timestamp + 1}-${Math.random().toString( 36 )
.slice( 2, 8 )}`;

    dispatch(
      { type: 'aiAssistant/message/addUser', payload: { id: userMsgId, content, timestamp }},
      { source: 'user' }
    );

    dispatch(
      { type: 'aiAssistant/message/addAssistantLoading', payload: { id: assistantMsgId, timestamp: timestamp + 1 }},
      { source: 'application' }
    );

    dispatch(
      { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: true }},
      { source: 'application' }
    );
  }, [ dispatch, isProcessing ]);

  const clearMessages = useCallback(() => {
    dispatch(
      { type: 'aiAssistant/conversation/new', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  const abortAssistantResponse = useCallback(() => {
    dispatch(
      { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
      { source: 'user' }
    );
    dispatch(
      { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
      { source: 'application' }
    );
    /**
     * Settling the pending bubble is NOT cosmetic.
     *
     * Lowering `isProcessing` alone left the message stuck at `isLoading: true`
     * forever, and `backendPolicy` finds the message to write into by looking
     * for a loading assistant message — so that orphan captured the NEXT
     * answer, and the user watched an old message rewrite itself instead of a
     * new one appearing. Abort has to close its own turn.
     */
    dispatch(
      { type: 'aiAssistant/message/settleLoading', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  const toggleAutoExecute = useCallback(() => {
    dispatch(
      { type: 'aiAssistant/settings/toggleAutoExecute', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  const executePlan = useCallback(( messageId: string ) => {
    dispatch(
      { type: 'aiAssistant/plan/startExecution', payload: { messageId }},
      { source: 'user' }
    );
  }, [dispatch]);

  const discardPlan = useCallback(( messageId: string ) => {
    dispatch(
      { type: 'aiAssistant/plan/discarded', payload: { messageId }},
      { source: 'user' }
    );
  }, [dispatch]);

  const abortPlan = useCallback(( messageId: string ) => {
    dispatch(
      { type: 'aiAssistant/plan/aborted', payload: { messageId }},
      { source: 'user' }
    );
  }, [dispatch]);

  const completeHumanStep = useCallback(( messageId: string ) => {
    dispatch(
      { type: 'aiAssistant/plan/humanStepCompleted', payload: { messageId }},
      { source: 'user' }
    );
  }, [dispatch]);

  /**
   * Rate an assistant answer (TV1-2417). One click, no confirmation, no way to fail —
   * see `feedbackPolicy` for why a thumbs-down must never produce an error.
   */
  const submitFeedback = useCallback(( messageId: string, rating: 'up' | 'down' ) => {
    dispatch(
      { type: 'aiAssistant/feedback/submit', payload: { messageId, rating }},
      { source: 'user' }
    );
  }, [dispatch]);

  return {
    submitFeedback,
    togglePanel,
    openPanel,
    setPanelMode,
    seedOnboarding,
    sendMessage,
    clearMessages,
    abortAssistantResponse,
    toggleAutoExecute,
    executePlan,
    discardPlan,
    abortPlan,
    completeHumanStep
  };
}
